<template>
  <input-autocomplete
    :input-classes="inputClasses"
    :container-classes="containerClasses"
    :input-id="inputId"
    :input-qaid="inputQaid"
    :input-placeholder="inputPlaceholder"
    :input-name="inputName"
    :input-required="inputRequired"
    :input-max-length="inputMaxLength"
    class="flex flex-1"
    :suggestion-list="suggestionList"
    :initial-value="setInitialValue"
    :is-auto-location-supported="isAutoLocationSupported"
    :query-geolocation="queryGeolocation"
    :is-geolocation-query-scheduled="isGeolocationQueryScheduled"
    :near-location-value="nearLocationValue"
    :is-auto-detecting-location="isAutoDetectingLocation"
    :is-address-line-input="isAddressLineInput"
    @input-updated="updateInputValue($event)"
  ></input-autocomplete>
</template>
<script>
import InputAutocomplete from '@/components/InputAutocomplete';
import {getSearchSuggestionList, MapboxGeocodeTypes} from '@/components/mapbox/utils/mapbox-api.js';
import debounce from 'lodash/debounce';
import {mapState, mapGetters} from 'vuex';

const DEFAULT_SUGGESTION_AMOUNT = 5;
const MIN_CHARACTER_SEARCH_COUNT = 3;
const SEARCH_SUGGESTIONS_DEBOUNCE_TIME = 250;
const INPUT_DELAY = 1000;

export default {
  components: {
    InputAutocomplete,
  },
  props: {
    suggestionAmount: {
      type: Number,
      default: DEFAULT_SUGGESTION_AMOUNT,
    },
    inputClasses: {
      type: String,
      default: '',
    },
    containerClasses: {
      type: String,
      default: '',
    },
    inputId: {
      type: String,
      default: '',
    },
    inputQaid: {
      type: String,
      default: '',
    },
    inputName: {
      type: String,
      default: '',
    },
    inputRequired: {
      type: String,
      default: '',
    },
    inputPlaceholder: {
      type: String,
      default: '',
    },
    inputMaxLength: {
      type: Number,
      default: 150,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    nearLocationValue: {
      type: String,
      default: '',
    },
    isAutoLocationSupported: {
      type: Boolean,
      default: false,
    },
    queryGeolocation: {
      type: Function,
      default: null,
    },
    isGeolocationQueryScheduled: {
      type: Boolean,
      default: false,
    },
    isAutoDetectingLocation: {
      type: Boolean,
      default: false,
    },
    isAddressLineInput: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: '',
      inputData: {},
      inputCenter: [],
      isSelecting: false,
      suggestionList: [],
      isShowingSuggestionList: false,
      placeNameIdentifier: '',
      selectedOnList: '',
    };
  },
  computed: {
    ...mapState('backend', ['geolocation', 'mapboxApiKey']),
    ...mapGetters('backend', ['countryCode']),
    setInitialValue() {
      if (this.isAddressLineInput) return this.nearLocationValue;
      return !this.nearLocationValue.length > 0 ? this.geolocation.address : this.nearLocationValue;
    },
  },
  watch: {
    inputValue() {
      if (this.isAddressLineInput && this.isSelecting) return;
      if (this.isAddressLineInput) {
        this.debounceUpdateInputOnEvent();
      } else {
        this.updateInputAndSuggestions();
      }
    },
  },
  methods: {
    updateInputValue({
      inputValue = '',
      data = {},
      isSearching = false,
      placeNameIdentifier = '',
      selectedOnList = false,
    }) {
      this.isSelecting = isSearching;
      this.placeNameIdentifier = placeNameIdentifier;
      this.selectedOnList = selectedOnList;
      this.inputValue = inputValue;
      this.inputData = data;
      if (isSearching) {
        this.updateInputAndSuggestions();
      }
    },
    debounceUpdateInputOnEvent: debounce(function () {
      this.updateInputAndSuggestions();
    }, INPUT_DELAY),
    updateInputAndSuggestions() {
      this.$emit('input-updated', {
        inputValue: this.inputValue,
        data: this.inputData,
        isSelecting: this.isSelecting,
        placeNameIdentifier: this.placeNameIdentifier,
        selectedOnList: this.selectedOnList,
      });
      this.selectedOnList = false;
      this.isSelecting = false;

      if (this.inputValue.length < MIN_CHARACTER_SEARCH_COUNT) {
        this.isShowingSuggestionList = false;
        this.suggestionList = [];
        this.emitOnSuggestionsUpdate(this.suggestionList, this.inputValue);
        return;
      }
      this.isShowingSuggestionList = true;
      this.updateSearchSuggestions(this.inputValue);
    },
    updateSearchSuggestions: debounce(async function (newValue) {
      try {
        let payloadObj = {
          search: newValue,
          accessToken: this.mapboxApiKey,
          country: this.countryCode,
        };
        if (this.isAddressLineInput) {
          payloadObj = {...payloadObj, returnSuggestion: this.isAddressLineInput, types: MapboxGeocodeTypes.ADDRESS};
        }
        await getSearchSuggestionList(payloadObj).then((suggestionList) => {
          if (this.isShowingSuggestionList) this.suggestionList = suggestionList;
        });
      } catch (error) {
        console.error(`Error with Mapbox API: ${error}`);
        this.suggestionList = [];
        this.isShowingSuggestionList = false;
      } finally {
        this.emitOnSuggestionsUpdate(this.suggestionList, newValue);
      }
    }, SEARCH_SUGGESTIONS_DEBOUNCE_TIME),
    emitOnSuggestionsUpdate(list, inputText) {
      if (this.isAddressLineInput) {
        this.$emit('input-suggestions-updated', {listLength: list.length, inputLength: inputText.length});
      } else {
        this.$emit('input-suggestions-updated', list);
      }
    },
  },
};
</script>
